import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import _ from "underscore";
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import {TablePagination, Select, MenuItem, useTheme} from "@material-ui/core";

import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import {
  ComboSelect,
  MaskedText,
} from "../../../controls";
import AirportLookup from "../../Airports/components/AirportsLookup";
import ContactMethods from "./ContactMethodList";
import { useCompanyContacts, useAddCompanyContact, useUpdateCompanyContact, useDeleteCompanyContact } from "../../../services/companies";
import CompanyContactsValidator from "../validators/CompanyContactsValidator";


const extractMethods = (contactMethods) => {
  const methods = [];
  if (contactMethods && Array.isArray(contactMethods)) {
    contactMethods.forEach(contactMethod => {
      Object.keys(contactMethod).forEach(function (key) {
        methods.push({
          contactMethod: key,
          contactValue: contactMethod[key],
        });
      });
    })
  }
  return methods;
}


const CompanyContactList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(CompanyContactsValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const [companyRecord, setCompanyRecord] = useState(props.record)

  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('CompanyManagementList.rowSelected', (record)=> {
      setCompanyRecord(record);
    });
  }

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddCompanyContact = useAddCompanyContact();
  const handleUpdateCompanyContact = useUpdateCompanyContact();
  const handleDeleteCompanyContact = useDeleteCompanyContact();

  const queryClient = useQueryClient();
  const extraKeys = [companyRecord.companyUuid];
  const companyContacts = useCompanyContacts({tableRef, queryClient, extraKeys});

  const contactTypes = SystemData.contactTypes;

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...ColumnWidth(150),
        ...baseColumn,
        title: "Contact Type",
        field: "contactType",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: contactTypes[1],
        editComponent: (props) => (
          <Select
            labelId="Contact Type"
            id="Contact Type"
            value={props.value}
            onChange={e => {
              props.onChange(e.target.value);
            }}
          >
            {contactTypes.map((item) => {
              return (<MenuItem key={item} value={item}>{item}</MenuItem>);
            })
            }
          </Select>
        ),
        render: (rowData) => (<div>{rowData.contactType}</div>),
      },
      {
        ...baseColumn,
        title: "Airport",
        field: "airportCode",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Title",
        field: "title",
        editable: ((cell,rowData,updatedData) => updatedData.contactType==="PERSON"?state.canEdit:false),
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(4)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Forename",
        field: "forename",
        editable: ((cell,rowData,updatedData) => updatedData.contactType==="PERSON"?state.canEdit:false),
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Initials",
        field: "initials",
        editable: ((cell,rowData,updatedData) => updatedData.contactType==="PERSON"?state.canEdit:false),
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Surname",
        field: "surname",
        editable: ((cell,rowData,updatedData) => updatedData.contactType==="PERSON"?state.canEdit:false),
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Role",
        field: "role",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            group="CONTACT ROLES"
            fullWidth
            value={rowData.role}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            group="CONTACT ROLES"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Contact Method",
        field: "contactMethods",
        editable: "never",
        render: rowData => {
          if ( ! _.isEmpty(rowData.contactMethods)) {
            const methods = extractMethods(rowData.contactMethods);
            const firstMethod = methods[0].contactMethod + ": " + methods[0].contactValue;
            return (<div>{firstMethod}</div>);
          }
          return (<div></div>);
        }
      },
    ];
  };

  const detailsChangeHandler = (changedFields, contactRecord, canEdit) => {
    const contactMethods = changedFields;
    new Promise((resolve, reject) => {
      handleUpdateCompanyContact.mutate({record: {...contactRecord, contactMethods, companyUuid: companyRecord.companyUuid}, oldRecord: undefined, resolve, reject, extraKeys});
    });
  }


  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            addRowPosition: "first",
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            padding: "dense",
            toolbarButtonAlignment: "right",
            pageSize: props.fullPage ? 15 : 5,
            detailPanelType: "single",
            restoreOpenDetailPanel: 'companyContactUuid',
            minBodyHeight: props.fullPage ? "calc(100vh - 340px)" : undefined, //FIXME to calculate dynamic height, needed for correct scroll position identification
            maxBodyHeight: props.fullPage ? "calc(100vh - 340px)" : undefined
          }}
          components={{
            ...defaultComponents,
              Pagination: (props) => (
                <TablePagination
                    {...props}
                    rowsPerPageOptions={[5, 10, 15]}
                />
            ),
          }}
          title={"Contacts" + (companyRecord.companyName === undefined ? " (No Company Selected)" : " for " + companyRecord.companyName)}
          columns={getColumns({canEdit: state.canEdit})}
          detailPanel={
            [
              {
                tooltip: "Contact Methods",
                icon: 'mail',
                openIcon: 'drafts',
                render: (rowData) => {
                  return (
                    <div >
                      <div style={{backgroundColor: "#f2f2f2"}}>
                        <ContactMethods
                          canEdit={state.canEdit}
                          contactMethods={rowData.contactMethods}
                          onChange={(changedData) => {detailsChangeHandler(changedData, rowData, !state.canEdit)}}
                          extractContacts={extractMethods}
                        />
                      </div>
                    </div>
                  );
                },
              }
            ]
          }
          data={companyContacts.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: companyRecord.companyUuid && state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  handleAddCompanyContact.mutate({record: {...newData, companyUuid: companyRecord.companyUuid}, resolve, reject, extraKeys});
                })
                : undefined,
            onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateCompanyContact.mutate({record: {...newData, companyUuid: companyRecord.companyUuid}, oldRecord: oldData, resolve, reject, extraKeys});
                }),
            onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteCompanyContact.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                }),
          }}
        />
      </div>
  );

}

export default CompanyContactList;
